import * as React from 'react';

import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { RangePickerProps, DatePickerProps } from 'antd/es/date-picker';
import { DatePicker } from 'antd';

import { getPopupContainer } from '@common/react/components/Utils/Utils';

const { RangePicker } = DatePicker;

dayjs.extend(utc);

const defaultDisabledDate = (currentDate?: Dayjs) => {
	if (currentDate) {
		const dateUtc = currentDate.utc();
		return dateUtc.isBefore('0001-01-01') || dateUtc.isAfter('9999-12-31 23:59:59');
	}
	return false;
};

const disabledAfterToday = (currentDate?: Dayjs) => (currentDate
	? currentDate.isBefore('0001-01-01') || currentDate.isAfter(dayjs(), 'day')
	: false);

const disabledBeforeToday = (currentDate?: Dayjs) => (currentDate
	? currentDate.isBefore(dayjs(), 'day') || currentDate.isAfter('9999-12-31 23:59:59')
	: false);

export const disabledFrom = (from: number | null, withoutToday?: boolean) => (currentDate?: Dayjs) => (
	currentDate
		? ((!withoutToday && currentDate.isBefore(dayjs(), 'day'))
			|| (from !== null ? currentDate.isBefore(dayjs(from), 'day') : false))
		: false
);

export const disabledTo = (to: number | null, withoutToday?: boolean) => (currentDate?: Dayjs) => (
	currentDate
		? ((!withoutToday && currentDate.isAfter(dayjs(), 'day'))
			|| (to !== null ? currentDate.isAfter(dayjs(to), 'day') : false))
		: false
);

interface ComponentProps {
	format?: string;
	placeholderRange?: [string, string];
	disabledDate?: (currentDate?: Dayjs) => boolean;
	maxDateToday?: boolean;
	minDateToday?: boolean;
	locale?: any;
	cellRender?: DatePickerProps['cellRender'];
	utc?: boolean;
}

export interface DatepickerProps extends ComponentProps {
	value: number | null;
	onChange: (date: number | null) => void;
	antdProps?: DatePickerProps<moment.Moment> & {showTime?: boolean};
	forceFormat?: boolean;
}

export interface RangepickerProps extends ComponentProps {
	value: [number, number] | null;
	onChange: (date: [number, number] | null) => void;
	antdProps?: RangePickerProps & { showTime?: boolean, popupClassName?: string };
	utcOffset?: string;
	forceFormat?: boolean;
}

const Datepicker: React.FC<DatepickerProps> = ({
	value, onChange, format = 'MM/DD/YYYY', maxDateToday, minDateToday, disabledDate, locale, antdProps = {}, cellRender, utc, forceFormat,
}) => {
	// tslint:disable-next-line:no-parameter-reassignment
	if (maxDateToday) disabledDate = disabledAfterToday;

	// tslint:disable-next-line:no-parameter-reassignment
	if (minDateToday) disabledDate = disabledBeforeToday;

	const defaultValue = value !== null && value !== undefined ? utc ? dayjs(value).utc() : dayjs(value) : undefined;

	const innerOnChange = (date: dayjs.Dayjs | null) => {
		onChange(date === null ? null : +date);
	};

	return <div className="datepicker-component">
		<DatePicker<any, any>
			{...antdProps}
			format={antdProps && antdProps.showTime && !forceFormat ? 'MM/DD/YYYY HH:mm' : format}
			onChange={innerOnChange}
			disabledDate={disabledDate}
			locale={locale}
			value={defaultValue}
			getPopupContainer={antdProps?.getPopupContainer || getPopupContainer}
			cellRender={cellRender}
		/>
	</div>;
};

export const Rangepicker: React.FC<RangepickerProps> = ({
	value,
	onChange,
	format = 'MM/DD/YYYY',
	placeholderRange = ['Start date', 'End date'],
	maxDateToday,
	minDateToday,
	disabledDate = defaultDisabledDate,
	locale,
	antdProps = {},
	utc = false,
	utcOffset = undefined,
	forceFormat,
}) => {
	const diff = utcOffset ? 60 * parseInt(utcOffset, 10) * 60 * 1000 : 0;
	const innerOnChange = (date: any) => {
		onChange(date?.length === 2 ? [
			utcOffset
				? dayjs(+date[0]).utc().startOf('day').valueOf() - diff
				: utc
					? dayjs(+date[0]).utc().startOf('day').valueOf()
					: dayjs(+date[0]).startOf('day').valueOf(),
			utcOffset
				? dayjs(+date[1]).utc().endOf('day').valueOf() - diff
				: utc
					? dayjs(+date[1]).utc().endOf('day').valueOf()
					: dayjs(+date[1]).endOf('day').valueOf(),
		] : null);
	};

	// tslint:disable-next-line:no-parameter-reassignment
	if (maxDateToday) disabledDate = disabledAfterToday;

	// tslint:disable-next-line:no-parameter-reassignment
	if (minDateToday) disabledDate = disabledBeforeToday;

	let defaultValue;

	if (value !== null && Number(value[0]) && Number(value[1])) {
		defaultValue = utcOffset
			? [dayjs(value[0] as number + diff).utc(), dayjs(value[1] as number + diff).utc()]
			: utc
				? [dayjs(value[0] as number).utc(), dayjs(value[1] as number).utc()]
				: [dayjs(value[0] as number), dayjs(value[1] as number)];
	} else {
		defaultValue = [null, null];
	}

	return <div className="datepicker-component">
		<RangePicker
			{...antdProps}
			format={antdProps && antdProps.showTime && !forceFormat ? 'MM/DD/YYYY HH:mm' : format}
			onChange={innerOnChange}
			disabledDate={disabledDate}
			locale={locale}
			value={defaultValue}
			getPopupContainer={antdProps?.getPopupContainer || getPopupContainer}
			placeholder={placeholderRange}
		/>
	</div>;
};

export default Datepicker;
